import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import * as api from '../../../api';
import { supabase } from '../../../lib/supabaseClient';
import Spinner from '../../Atoms/Spinner';
import toast from 'react-hot-toast';
import { getEventDesignerDisplayName } from '../../../Components/AdminPanels/utils/eventDesignerHelpers';

const Container = styled.div`
    max-width: 1600px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Header = styled.div`
    margin-bottom: 40px;

    h1 {
        font-size: 32px;
        margin-bottom: 8px;
        color: #333;
    }

    .subtitle {
        font-size: 18px;
        color: #666;
    }
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    margin-bottom: 20px;

    &:hover {
        background: #e8e8e8;
        transform: translateX(-4px);
    }
`;

const StatsSection = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
`;

const StatCard = styled.div`
    background: ${props => props.color || 'white'};
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);

    .value {
        font-size: 36px;
        font-weight: 700;
        color: ${props => props.textColor || '#333'};

        .total-suffix {
            font-size: 18px;
            font-weight: 500;
            opacity: 0.5;
        }
    }

    .label {
        font-size: 14px;
        color: ${props => props.textColor ? `${props.textColor}aa` : '#666'};
        margin-top: 8px;
    }

    .detail {
        display: flex;
        gap: 12px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid ${props => props.textColor ? `${props.textColor}22` : '#eee'};

        .detail-item {
            font-size: 12px;
            font-weight: 500;
            padding: 4px 8px;
            border-radius: 4px;
            background: ${props => props.textColor ? `${props.textColor}15` : '#f0f0f0'};
            color: ${props => props.textColor || '#666'};

            &.logo::before {
                content: '🖼 ';
            }

            &.email::before {
                content: '✉ ';
            }
        }
    }
`;

const TabsContainer = styled.div`
    display: flex;
    gap: 0;
    margin-bottom: 30px;
    border-bottom: 2px solid #e0e0e0;
`;

const Tab = styled.button`
    padding: 16px 32px;
    background: ${props => props.active ? 'white' : 'transparent'};
    border: none;
    border-bottom: 3px solid ${props => props.active ? '#9b59b6' : 'transparent'};
    font-size: 16px;
    font-weight: ${props => props.active ? '600' : '500'};
    color: ${props => props.active ? '#9b59b6' : '#666'};
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: -2px;

    &:hover {
        color: #9b59b6;
        background: ${props => props.active ? 'white' : '#f8f8f8'};
    }
`;

const TableSection = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #eee;
    flex-wrap: wrap;
    gap: 16px;

    h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`;

const SearchInput = styled.input`
    padding: 10px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    width: 300px;

    &:focus {
        outline: none;
        border-color: #9b59b6;
    }
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #9b59b6, #8e44ad)' :
                          props.variant === 'success' ? 'linear-gradient(135deg, #27ae60, #229954)' :
                          props.variant === 'danger' ? 'linear-gradient(135deg, #e74c3c, #c0392b)' :
                          '#f5f5f5'};
    color: ${props => props.variant ? 'white' : '#333'};
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th {
        text-align: left;
        padding: 16px 16px;
        background: #f8f9fa;
        font-weight: 600;
        font-size: 13px;
        color: #555;
        border-bottom: 2px solid #eee;
    }

    td {
        padding: 16px 16px;
        border-bottom: 1px solid #f0f0f0;
        font-size: 14px;
        color: #333;
        vertical-align: middle;
    }

    tr:hover td {
        background: #f8f9fa;
    }
`;

const LogoCell = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 8px;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #e0e0e0;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .placeholder {
        font-size: 24px;
        color: #ccc;
    }
`;

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    background: ${props => props.type === 'fabricant' ? '#e3f2fd' : props.type === 'ecole' ? '#f3e5f5' : props.type === 'distributeur' ? '#e0f2f1' : props.type === 'lieu' ? '#fff8e1' : props.type === 'institution' ? '#fff3e0' : props.type === 'officiel' ? '#e8eaf6' : '#fce4ec'};
    color: ${props => props.type === 'fabricant' ? '#1976d2' : props.type === 'ecole' ? '#7b1fa2' : props.type === 'distributeur' ? '#00796b' : props.type === 'lieu' ? '#f9a825' : props.type === 'institution' ? '#e65100' : props.type === 'officiel' ? '#3f51b5' : '#c2185b'};
`;

const ProjectsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ProjectCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    background: ${props => props.count > 0 ? '#d4edda' : '#f8d7da'};
    color: ${props => props.count > 0 ? '#155724' : '#721c24'};
`;

const ProjectThumbnails = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 320px;
`;

const ProjectThumbnailWrapper = styled.div`
    position: relative;
    width: 48px;
    height: 48px;

    &:hover {
        z-index: 100;
    }
`;

const ProjectThumbnail = styled.div`
    position: relative;
    width: 48px;
    height: 48px;
    border-radius: 4px;
    overflow: hidden;
    border: 2px solid ${props => props.isPrivate ? '#e74c3c' : '#ddd'};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        transform: scale(1.1);
        border-color: ${props => props.isPrivate ? '#c0392b' : '#9b59b6'};
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .placeholder {
        width: 100%;
        height: 100%;
        background: #f0f0f0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: #999;
    }

    .privacy-icon {
        position: absolute;
        top: -2px;
        right: -2px;
        background: #e74c3c;
        color: white;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
`;

const Tooltip = styled.div`
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: white;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 11px;
    white-space: nowrap;
    z-index: 1000;
    margin-bottom: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;

    ${ProjectThumbnailWrapper}:hover & {
        opacity: 1;
    }

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 5px solid transparent;
        border-top-color: #333;
    }

    .privacy-note {
        color: #ff8a80;
        font-size: 10px;
        margin-top: 2px;
    }
`;

const ActionButtonSmall = styled.button`
    padding: 6px 12px;
    background: ${props => props.variant === 'danger' ? '#fee' : '#f0f0f0'};
    color: ${props => props.variant === 'danger' ? '#c0392b' : '#333'};
    border: 1px solid ${props => props.variant === 'danger' ? '#fcc' : '#ddd'};
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s ease;
    margin-right: 8px;

    &:hover {
        background: ${props => props.variant === 'danger' ? '#fdd' : '#e8e8e8'};
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 16px;
    padding: 32px;
    max-width: 600px;
    width: 90%;
    max-height: 85vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;

    h3 {
        margin: 0;
        font-size: 20px;
        color: #333;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: #999;
    padding: 0;
    line-height: 1;

    &:hover {
        color: #333;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 20px;

    label {
        display: block;
        font-weight: 600;
        margin-bottom: 8px;
        color: #333;
        font-size: 14px;
    }

    input, select {
        width: 100%;
        padding: 12px 16px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
        transition: border-color 0.2s;

        &:focus {
            outline: none;
            border-color: #9b59b6;
        }
    }
`;

const LogoUploadArea = styled.div`
    border: 2px dashed #ddd;
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #fafafa;

    &:hover {
        border-color: #9b59b6;
        background: #f8f0fc;
    }

    &.has-logo {
        border-style: solid;
        border-color: #9b59b6;
    }

    img {
        max-width: 150px;
        max-height: 100px;
        object-fit: contain;
        margin-bottom: 12px;
    }

    .upload-text {
        color: #666;
        font-size: 14px;
    }

    .change-text {
        color: #9b59b6;
        font-size: 13px;
        margin-top: 8px;
    }
`;

const ProjectsList = styled.div`
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 8px;
`;

const ProjectItem = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
    background: ${props => props.isLinked ? '#e8f5e9' : 'transparent'};
    border-left: ${props => props.isLinked ? '3px solid #4caf50' : '3px solid transparent'};

    &:hover {
        background: ${props => props.isLinked ? '#c8e6c9' : '#f5f5f5'};
    }

    input {
        width: auto;
    }

    .project-info {
        flex: 1;

        .name {
            font-weight: 500;
            font-size: 14px;
        }

        .designer {
            font-size: 12px;
            color: #666;
        }
    }
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #eee;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: #999;

    .icon {
        font-size: 48px;
        margin-bottom: 16px;
    }

    .message {
        font-size: 16px;
        margin-bottom: 8px;
    }

    .hint {
        font-size: 14px;
        color: #bbb;
    }
`;

const LinkButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: #e8f5e9;
    color: #2e7d32;
    border-radius: 6px;
    font-size: 12px;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
        background: #c8e6c9;
        text-decoration: none;
    }
`;

const LastUpdate = styled.div`
    font-size: 13px;
    color: #888;
    margin-top: 16px;
    text-align: right;
    padding-right: 24px;
`;

// List of winner project IDs for FD100 2025
const WINNER_PROJECT_IDS = [
    '67a332e21b103d938bafb0dc', '67a0b8331b103d938bafa954', '67aa1011740d844a3d633e93',
    '6829daa5754f51df2ef9141c', '67e6c18043d5609350e381a8', '67b5e6146890f1a147192905',
    '67be4a58b1ba28c3144aeb73', '67bf2330b1ba28c3144af8d2', '67c090dcdb48cb16a13c5b43',
    '677268097ec5d90c89d6fe63', '67c6b416db48cb16a13c73c7', '67ab652c07736060460b3b75',
    '68348986754f51df2ef9b87c', '67d15108d95ccf24eaf191df', '67d7d38c43d5609350e242a1',
    '67d82d2243d5609350e24c5e', '662adc507159e5b1b1da7c75', '67d8718c43d5609350e25c38',
    '67d1977243d5609350e218f2', '682b5c5f754f51df2ef93bbc', '67da910a43d5609350e274d7',
    '67e1864b43d5609350e2baeb', '67daebf343d5609350e27a9e', '67dd585043d5609350e28e57',
    '67dfeb0e43d5609350e2a360', '67e1452a43d5609350e2b066', '678675e2de3ed4a7001ab532',
    '67e2d05943d5609350e2cf97', '67e3e1cb43d5609350e304e5', '67e3e81543d5609350e308de',
    '67e3f02643d5609350e30a0a', '67e42d7043d5609350e32539', '67e42e5543d5609350e32570',
    '67e45c9843d5609350e329c0', '67e529f743d5609350e33a8c', '67e53aab43d5609350e340c3',
    '67e56ab043d5609350e3490f', '67e5764d43d5609350e34cb5', '67e57f5143d5609350e3519a',
    '67e5864c43d5609350e353a3', '67e58da443d5609350e3561d', '67e58ebc43d5609350e35665',
    '67e5ab6b43d5609350e35905', '67e6828d43d5609350e36b6a', '67e6acaf43d5609350e37832',
    '67e51ddb43d5609350e33667', '67e6c0a843d5609350e380cf', '67e6d53a43d5609350e38725',
    '67e6dfff43d5609350e38900', '67e7003c43d5609350e38bfc', '6834625c754f51df2ef9a4b1',
    '67ea99b76b7945ed1a576bae', '67f90250a0262e085d37088f', '67e1741a43d5609350e2b608',
    '67fe7a6c9167c049fff4e37e', '67ab6ade07736060460b3c16', '6810a343264e7745556acae1',
    '6800f4c59167c049fff51f5d', '6800b8529167c049fff50e47', '6800e76e9167c049fff5197f',
    '6800ef849167c049fff51c6f', '6800fad59167c049fff52224', '680113739167c049fff52d73',
    '680106139167c049fff525af', '680125219167c049fff53405', '680957ab9167c049fff5a7c2',
    '681164d36c8fbfe5e3c91e9e', '68020ef79167c049fff54015', '68025d069167c049fff56132',
    '680b728c9167c049fff5c8f7', '680249249167c049fff55a19', '68024ed89167c049fff55b90',
    '68025b1a9167c049fff56037', '680270379167c049fff566f5', '680274c09167c049fff567c8',
    '680275419167c049fff567de', '68027b389167c049fff56954', '680291609167c049fff56cad',
    '6802a3fc9167c049fff56f5f', '6849787b17dde4ca4c4bf043', '6849435a17dde4ca4c4be251',
    '6807cb759167c049fff58dd4', '6808c3f59167c049fff599c7', '6808c3859167c049fff599b1',
    '680fa4b19167c049fff5deee', '6801e2f49167c049fff53ce1', '680a54a09167c049fff5bbc4',
    '680b94b29167c049fff5cd49', '6810df4a264e7745556ad75d', '680fa3f89167c049fff5deb1',
    '680fa844a71ddf126b77b977', '680fa9f09167c049fff5e027', '6810c9b9264e7745556acf4d',
    '681125b66c8fbfe5e3c91c62', '6818cac19e656d77bc6c72fa', '6818d27e9e656d77bc6c75a3',
    '6818dbdf9e656d77bc6c78a0', '682d97b2754f51df2ef96070', '67fccffaa0262e085d370ddc',
    '680271809167c049fff5674a'
];

const FD100Partenaires = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const eventId = searchParams.get('id') || '67590da5499bcc995c210b40';

    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [activeTab, setActiveTab] = useState('fabricant');
    const [searchTerm, setSearchTerm] = useState('');
    const [partenaires, setPartenaires] = useState([]);
    const [winningProjects, setWinningProjects] = useState([]);
    const [trackingData, setTrackingData] = useState({});
    const [lastUpdate, setLastUpdate] = useState(null);
    const [editedDataMap, setEditedDataMap] = useState({});
    const [fabricantContactsMap, setFabricantContactsMap] = useState({});

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [editingPartenaire, setEditingPartenaire] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        type: 'fabricant',
        emails: [''], // Array of emails
        logo_urls: [''], // Array of logo URLs (first one displayed)
        linked_project_ids: []
    });
    const [uploadingLogo, setUploadingLogo] = useState(null); // Index of logo being uploaded
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
    }, [eventId]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch winning projects from API (light version — skips HubSpot calls)
            // Pass winnerProjectIds so server strips company projects to only matching ones
            const response = await api.getParticipatingUsersProjectData({ eventId, winnerProjectIds: WINNER_PROJECT_IDS });
            const winners = [];
            response.data.forEach(user => {
                if (user.participation.data.projets) {
                    user.participation.data.projets.forEach(project_id => {
                        const project = user.company.projects.find((projet) => projet._id === project_id);
                        if (project && project.status === "validé" && WINNER_PROJECT_IDS.includes(project._id)) {
                            // Get first image for thumbnail from medias array
                            let imageUrl = null;
                            if (project.medias && project.medias.length > 0) {
                                const firstMedia = project.medias[0];
                                if (firstMedia.type && firstMedia.type.includes('image')) {
                                    imageUrl = process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + firstMedia.path;
                                } else if (firstMedia.type && firstMedia.type.includes('pdf')) {
                                    // Use PDF preview image
                                    const filename = firstMedia.path.split('/').pop();
                                    const pdfPreviewPath = firstMedia.path.replace(filename, "firstpage_" + filename).replace('.pdf', '.png');
                                    imageUrl = process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + pdfPreviewPath;
                                }
                            }

                            winners.push({
                                project,
                                user,
                                projectId: project._id,
                                projectName: project.name_en || project.name,
                                designerName: getEventDesignerDisplayName(project, user),
                                imageUrl,
                                communiquerFabricants: project.communiquer_fabricants // 'oui' or 'non'
                            });
                        }
                    });
                }
            });
            setWinningProjects(winners);

            // Fetch tracking data for edited fabricants/schools
            const { data: tracking, error: trackingError } = await supabase
                .from('fd100_laureats_tracking')
                .select('*')
                .eq('event_id', eventId);

            if (!trackingError) {
                const trackingMap = {};
                tracking?.forEach(record => {
                    trackingMap[record.project_id] = record;
                });
                setTrackingData(trackingMap);
            }

            // Fetch partenaires from Supabase
            const { data: partenairesData, error } = await supabase
                .from('fd100_partenaires')
                .select('*')
                .eq('event_id', eventId)
                .order('name');

            if (error) {
                console.error('Error fetching partenaires:', error);
                toast.error('Erreur lors du chargement des partenaires');
            } else {
                setPartenaires(partenairesData || []);
            }

            setLastUpdate(new Date());

            // Fetch edited data from Supabase for display consistency
            const projectIds = winners.map(w => w.projectId);
            if (projectIds.length > 0) {
                const { data: trackingData, error: trackingEditError } = await supabase
                    .from('fd100_laureats_tracking')
                    .select('project_id, edited_project_name, edited_creator_name')
                    .in('project_id', projectIds);

                if (!trackingEditError && trackingData) {
                    const dataMap = {};
                    trackingData.forEach(record => {
                        dataMap[record.project_id] = {
                            projectName: record.edited_project_name,
                            creatorName: record.edited_creator_name
                        };
                    });
                    setEditedDataMap(dataMap);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    // Extract unique fabricants and ecoles from projects
    const extractPartenairesFromProjects = useCallback(() => {
        const fabricantsMap = new Map();
        const ecolesMap = new Map();

        // Debug: track all emails found
        const debugEmailsFound = [];
        const debugEmailsLost = [];

        // Debug: count sources
        let sourceStats = { fromTracking: 0, fromProject: 0, noFabricants: 0 };
        const debugAllFabricantsRaw = [];
        const debugLostEmailsFromTracking = []; // Emails in project but lost when using tracking

        winningProjects.forEach(({ project, projectId }) => {
            const tracking = trackingData[projectId];

            // Get fabricants - prefer edited version
            // Each fabricant object has: name, work, contact, email
            let fabricantsData = [];
            let source = 'none';

            // Parse both sources to compare
            let trackingFabs = [];
            let projectFabs = [];

            if (tracking?.edited_fabricants) {
                try {
                    const edited = typeof tracking.edited_fabricants === 'string'
                        ? JSON.parse(tracking.edited_fabricants)
                        : tracking.edited_fabricants;
                    trackingFabs = Array.isArray(edited) ? edited : [];
                } catch { }
            }

            if (project.fabricants) {
                try {
                    const parsed = JSON.parse(project.fabricants);
                    projectFabs = Array.isArray(parsed) ? parsed : [];
                } catch { }
            }

            // Use tracking if available, otherwise project
            // BUT: merge emails from project.fabricants if missing in tracking
            if (trackingFabs.length > 0) {
                // Create a map of project emails by normalized name for quick lookup
                const projectEmailsByName = new Map();
                projectFabs.forEach(pFab => {
                    const pName = typeof pFab === 'string' ? pFab : pFab.name;
                    const pEmail = typeof pFab === 'object' ? pFab.email : null;
                    if (pName && pEmail && pEmail.trim()) {
                        projectEmailsByName.set(pName.trim().toLowerCase(), pEmail.trim());
                    }
                });

                // Merge: use tracking data but fill in missing emails from project
                fabricantsData = trackingFabs.map(tFab => {
                    const tName = typeof tFab === 'string' ? tFab : tFab.name;
                    const tEmail = typeof tFab === 'object' ? tFab.email : null;

                    // If tracking has no email, try to get it from project
                    if ((!tEmail || !tEmail.trim()) && tName) {
                        const projectEmail = projectEmailsByName.get(tName.trim().toLowerCase());
                        if (projectEmail) {
                            // Return merged object with email from project
                            if (typeof tFab === 'string') {
                                return { name: tFab, email: projectEmail };
                            } else {
                                return { ...tFab, email: projectEmail };
                            }
                        }
                    }
                    return tFab;
                });

                // Also add any fabricants from project that have emails but are not in tracking at all
                projectFabs.forEach(pFab => {
                    const pName = typeof pFab === 'string' ? pFab : pFab.name;
                    const pEmail = typeof pFab === 'object' ? pFab.email : null;
                    if (pName && pEmail && pEmail.trim()) {
                        // Check if this fabricant exists in tracking (by name)
                        const existsInTracking = trackingFabs.some(tFab => {
                            const tName = typeof tFab === 'string' ? tFab : tFab.name;
                            return tName && tName.trim().toLowerCase() === pName.trim().toLowerCase();
                        });
                        if (!existsInTracking) {
                            // Add this fabricant from project (it was removed from tracking but has email)
                            fabricantsData.push(pFab);
                            debugLostEmailsFromTracking.push({
                                projectId,
                                name: pName,
                                email: pEmail,
                                reason: 'Fabricant with email not in tracking - RECOVERED from project'
                            });
                        }
                    }
                });

                source = 'tracking+merged';
                sourceStats.fromTracking++;
            } else if (projectFabs.length > 0) {
                fabricantsData = projectFabs;
                source = 'project';
                sourceStats.fromProject++;
            } else {
                sourceStats.noFabricants++;
            }

            // Log all fabricants with their emails for debugging
            fabricantsData.forEach(fab => {
                const name = typeof fab === 'string' ? fab : fab.name;
                const email = typeof fab === 'object' ? fab.email : null;
                if (name && email) {
                    debugAllFabricantsRaw.push({ projectId, name, email, source });
                }
            });

            // NOTE: communiquer_fabricants is a privacy option (oui/non), not additional fabricants
            // Filter out invalid values that might appear in fabricants data
            const invalidNames = ['oui', 'non', 'yes', 'no', ''];

            fabricantsData.forEach(fab => {
                // Handle both object format {name, email, ...} and string format
                const name = typeof fab === 'string' ? fab : fab.name;
                // Get email and ensure it's a valid non-empty string
                const rawEmail = typeof fab === 'object' ? fab.email : null;
                const email = rawEmail && typeof rawEmail === 'string' && rawEmail.trim() ? rawEmail.trim() : null;

                if (!name) return;
                const normalizedName = name.trim();
                // Skip invalid/privacy values
                if (normalizedName && !invalidNames.includes(normalizedName.toLowerCase())) {
                    const key = normalizedName.toLowerCase();

                    // Get contact name from fabricant object
                    const contact = typeof fab === 'object' ? (fab.contact || '').trim() : '';

                    if (!fabricantsMap.has(key)) {
                        fabricantsMap.set(key, {
                            name: normalizedName,
                            email: email,
                            contact: contact || null,
                            projectIds: new Set()
                        });
                        if (email) {
                            debugEmailsFound.push({ name: normalizedName, email, projectId, source: 'new' });
                        }
                    } else {
                        // If we already have this fabricant, try to add/update email
                        const existing = fabricantsMap.get(key);
                        if (email) {
                            if (!existing.email) {
                                // No email yet, add this one
                                existing.email = email;
                                debugEmailsFound.push({ name: normalizedName, email, projectId, source: 'merged' });
                            } else if (existing.email !== email) {
                                // Different email found - log it but keep the first one
                                debugEmailsLost.push({
                                    name: normalizedName,
                                    keptEmail: existing.email,
                                    lostEmail: email,
                                    projectId
                                });
                            }
                        }
                        // Fill contact if missing
                        if (!existing.contact && contact) {
                            existing.contact = contact;
                        }
                    }
                    fabricantsMap.get(key).projectIds.add(projectId);
                }
            });

            // Get ecoles - prefer edited version, handle "/" separator
            let schoolStr = tracking?.edited_school || project.school || '';
            // Also check hsProfile.school
            if (!schoolStr && winningProjects.find(w => w.projectId === projectId)?.user?.hsProfile?.school) {
                schoolStr = winningProjects.find(w => w.projectId === projectId).user.hsProfile.school;
            }

            if (schoolStr) {
                const schools = schoolStr.split('/').map(s => s.trim()).filter(Boolean);
                schools.forEach(name => {
                    const normalizedName = name.trim();
                    if (normalizedName) {
                        if (!ecolesMap.has(normalizedName.toLowerCase())) {
                            ecolesMap.set(normalizedName.toLowerCase(), { name: normalizedName, projectIds: new Set() });
                        }
                        ecolesMap.get(normalizedName.toLowerCase()).projectIds.add(projectId);
                    }
                });
            }
        });

        // Log debug info for fabricants emails
        console.log('=== FABRICANTS EMAIL DEBUG ===');
        console.log('Source stats:', sourceStats);
        console.log(`Total raw fabricants with emails (before dedup): ${debugAllFabricantsRaw.length}`);
        console.log('All raw fabricants with emails:', debugAllFabricantsRaw);
        console.log(`Total unique fabricants: ${fabricantsMap.size}`);
        console.log(`Fabricants with emails (after dedup): ${Array.from(fabricantsMap.values()).filter(f => f.email).length}`);
        console.log('Unique emails found:', debugEmailsFound);
        if (debugEmailsLost.length > 0) {
            console.warn('Duplicate fabricants with different emails (kept first):', debugEmailsLost);
        }
        if (debugLostEmailsFromTracking.length > 0) {
            console.error(`🚨 LOST EMAILS: ${debugLostEmailsFromTracking.length} emails exist in project.fabricants but are MISSING in tracking.edited_fabricants:`, debugLostEmailsFromTracking);
        }

        // Get distributeurs from projects (project.distributeur field)
        const distributeursMap = new Map();
        // Get editeurs/galeries from projects (project.editeur field) - separate from distributeurs
        const editeursMap = new Map();

        winningProjects.forEach(({ project, projectId }) => {
            // Get distributeur
            const distributeur = project.distributeur?.trim();
            if (distributeur) {
                const normalizedName = distributeur.toLowerCase();
                if (!distributeursMap.has(normalizedName)) {
                    distributeursMap.set(normalizedName, {
                        name: distributeur,
                        email: null,
                        projectIds: new Set()
                    });
                }
                distributeursMap.get(normalizedName).projectIds.add(projectId);
            }

            // Get editeur (galerie) - stored in SEPARATE map
            const editeur = project.editeur?.trim();
            if (editeur) {
                const normalizedName = editeur.toLowerCase();
                if (!editeursMap.has(normalizedName)) {
                    editeursMap.set(normalizedName, {
                        name: editeur,
                        email: null,
                        projectIds: new Set()
                    });
                }
                editeursMap.get(normalizedName).projectIds.add(projectId);
            }
        });

        return {
            fabricants: Array.from(fabricantsMap.values()).map(f => ({ ...f, projectIds: Array.from(f.projectIds), contact: f.contact || null })),
            ecoles: Array.from(ecolesMap.values()).map(e => ({ ...e, projectIds: Array.from(e.projectIds) })),
            distributeurs: Array.from(distributeursMap.values()).map(d => ({ ...d, projectIds: Array.from(d.projectIds) })),
            editeurs: Array.from(editeursMap.values()).map(ed => ({ ...ed, projectIds: Array.from(ed.projectIds) }))
        };
    }, [winningProjects, trackingData]);

    // Build fabricant contacts map from extracted data
    useEffect(() => {
        if (winningProjects.length === 0) return;
        const { fabricants } = extractPartenairesFromProjects();
        const contactsMap = {};
        fabricants.forEach(fab => {
            if (fab.contact) {
                contactsMap[fab.name.toLowerCase()] = fab.contact;
            }
        });
        setFabricantContactsMap(contactsMap);
    }, [winningProjects, trackingData, extractPartenairesFromProjects]);

    // Sync partenaires from projects
    const syncPartenaires = async () => {
        setSyncing(true);
        try {
            const { fabricants, ecoles, distributeurs, editeurs } = extractPartenairesFromProjects();
            let addedCount = 0;
            let updatedCount = 0;
            let deletedCount = 0;

            // Clean up invalid entries (oui, non, etc.) that may have been created previously
            const invalidNames = ['oui', 'non', 'yes', 'no'];
            const invalidPartenaires = partenaires.filter(p =>
                invalidNames.includes(p.name.toLowerCase())
            );
            for (const invalid of invalidPartenaires) {
                await supabase
                    .from('fd100_partenaires')
                    .delete()
                    .eq('id', invalid.id);
                deletedCount++;
            }

            // Get all valid winning project IDs for filtering
            const validProjectIds = new Set(WINNER_PROJECT_IDS);

            // Process fabricants
            for (const fab of fabricants) {
                const existing = partenaires.find(
                    p => p.type === 'fabricant' && p.name.toLowerCase() === fab.name.toLowerCase()
                );

                if (existing) {
                    // Filter existing linked_project_ids to only include winning projects, then merge with new ones
                    const filteredExisting = (existing.linked_project_ids || []).filter(id => validProjectIds.has(id));
                    const newProjectIds = [...new Set([...filteredExisting, ...fab.projectIds])];
                    // Check if we need to update project IDs or email
                    const needsProjectIdUpdate = JSON.stringify(newProjectIds.sort()) !== JSON.stringify((existing.linked_project_ids || []).sort());
                    // Check if we need to add email (only if no emails exist yet)
                    const existingEmails = existing.emails || (existing.email ? [existing.email] : []);
                    const needsEmailUpdate = existingEmails.length === 0 && fab.email;

                    if (needsProjectIdUpdate || needsEmailUpdate) {
                        const updateData = { linked_project_ids: newProjectIds };
                        if (needsEmailUpdate) {
                            updateData.emails = [fab.email];
                        }
                        await supabase
                            .from('fd100_partenaires')
                            .update(updateData)
                            .eq('id', existing.id);
                        updatedCount++;
                    }
                } else {
                    // Create new with email if available
                    await supabase
                        .from('fd100_partenaires')
                        .insert({
                            event_id: eventId,
                            name: fab.name,
                            type: 'fabricant',
                            emails: fab.email ? [fab.email] : null,
                            linked_project_ids: fab.projectIds
                        });
                    addedCount++;
                }
            }

            // Process ecoles
            for (const ecole of ecoles) {
                const existing = partenaires.find(
                    p => p.type === 'ecole' && p.name.toLowerCase() === ecole.name.toLowerCase()
                );

                if (existing) {
                    // Filter existing linked_project_ids to only include winning projects, then merge with new ones
                    const filteredExisting = (existing.linked_project_ids || []).filter(id => validProjectIds.has(id));
                    const newProjectIds = [...new Set([...filteredExisting, ...ecole.projectIds])];
                    // Always update to ensure we clean up any non-winner project IDs
                    if (JSON.stringify(newProjectIds.sort()) !== JSON.stringify((existing.linked_project_ids || []).sort())) {
                        await supabase
                            .from('fd100_partenaires')
                            .update({ linked_project_ids: newProjectIds })
                            .eq('id', existing.id);
                        updatedCount++;
                    }
                } else {
                    await supabase
                        .from('fd100_partenaires')
                        .insert({
                            event_id: eventId,
                            name: ecole.name,
                            type: 'ecole',
                            linked_project_ids: ecole.projectIds
                        });
                    addedCount++;
                }
            }

            // Process distributeurs (from project.distributeur field)
            for (const dist of distributeurs) {
                const existing = partenaires.find(
                    p => p.type === 'distributeur' && p.name.toLowerCase() === dist.name.toLowerCase()
                );

                if (existing) {
                    // Filter existing linked_project_ids to only include winning projects, then merge with new ones
                    const filteredExisting = (existing.linked_project_ids || []).filter(id => validProjectIds.has(id));
                    const newProjectIds = [...new Set([...filteredExisting, ...dist.projectIds])];
                    // Check if we need to update
                    if (JSON.stringify(newProjectIds.sort()) !== JSON.stringify((existing.linked_project_ids || []).sort())) {
                        await supabase
                            .from('fd100_partenaires')
                            .update({ linked_project_ids: newProjectIds })
                            .eq('id', existing.id);
                        updatedCount++;
                    }
                } else {
                    await supabase
                        .from('fd100_partenaires')
                        .insert({
                            event_id: eventId,
                            name: dist.name,
                            type: 'distributeur',
                            linked_project_ids: dist.projectIds
                        });
                    addedCount++;
                }
            }

            // Process editeurs/galeries (from project.editeur field) - SEPARATE from distributeurs
            for (const ed of editeurs) {
                const existing = partenaires.find(
                    p => p.type === 'editeur' && p.name.toLowerCase() === ed.name.toLowerCase()
                );

                if (existing) {
                    // Filter existing linked_project_ids to only include winning projects, then merge with new ones
                    const filteredExisting = (existing.linked_project_ids || []).filter(id => validProjectIds.has(id));
                    const newProjectIds = [...new Set([...filteredExisting, ...ed.projectIds])];
                    // Check if we need to update
                    if (JSON.stringify(newProjectIds.sort()) !== JSON.stringify((existing.linked_project_ids || []).sort())) {
                        await supabase
                            .from('fd100_partenaires')
                            .update({ linked_project_ids: newProjectIds })
                            .eq('id', existing.id);
                        updatedCount++;
                    }
                } else {
                    await supabase
                        .from('fd100_partenaires')
                        .insert({
                            event_id: eventId,
                            name: ed.name,
                            type: 'editeur',
                            linked_project_ids: ed.projectIds
                        });
                    addedCount++;
                }
            }

            await fetchData();
            const parts = [];
            if (addedCount > 0) parts.push(`${addedCount} ajoutés`);
            if (updatedCount > 0) parts.push(`${updatedCount} mis à jour`);
            if (deletedCount > 0) parts.push(`${deletedCount} supprimés`);
            toast.success(`Synchronisation terminée: ${parts.length > 0 ? parts.join(', ') : 'aucun changement'}`);
        } catch (error) {
            console.error('Error syncing partenaires:', error);
            toast.error('Erreur lors de la synchronisation');
        } finally {
            setSyncing(false);
        }
    };

    // Handle logo upload for a specific index
    const handleLogoUpload = async (e, index) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingLogo(index);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${eventId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from('fd100-partenaires-logos')
                .upload(fileName, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('fd100-partenaires-logos')
                .getPublicUrl(fileName);

            setFormData(prev => {
                const newLogoUrls = [...prev.logo_urls];
                newLogoUrls[index] = publicUrl;
                return { ...prev, logo_urls: newLogoUrls };
            });
            toast.success('Logo uploadé');
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error('Erreur lors de l\'upload du logo');
        } finally {
            setUploadingLogo(null);
        }
    };

    // Add new email field
    const addEmailField = () => {
        setFormData(prev => ({ ...prev, emails: [...prev.emails, ''] }));
    };

    // Remove email field
    const removeEmailField = (index) => {
        setFormData(prev => ({
            ...prev,
            emails: prev.emails.filter((_, i) => i !== index)
        }));
    };

    // Update email at index
    const updateEmail = (index, value) => {
        setFormData(prev => {
            const newEmails = [...prev.emails];
            newEmails[index] = value;
            return { ...prev, emails: newEmails };
        });
    };

    // Add new logo field
    const addLogoField = () => {
        setFormData(prev => ({ ...prev, logo_urls: [...prev.logo_urls, ''] }));
    };

    // Remove logo field
    const removeLogoField = (index) => {
        setFormData(prev => ({
            ...prev,
            logo_urls: prev.logo_urls.filter((_, i) => i !== index)
        }));
    };

    // Save partenaire
    const savePartenaire = async () => {
        if (!formData.name.trim()) {
            toast.error('Le nom est requis');
            return;
        }

        setSaving(true);
        try {
            // Filter out empty emails and logo_urls
            const filteredEmails = formData.emails.map(e => e.trim()).filter(e => e);
            const filteredLogoUrls = formData.logo_urls.filter(url => url);

            const dataToSave = {
                event_id: eventId,
                name: formData.name.trim(),
                type: formData.type,
                emails: filteredEmails.length > 0 ? filteredEmails : null,
                logo_urls: filteredLogoUrls.length > 0 ? filteredLogoUrls : null,
                linked_project_ids: formData.linked_project_ids
            };

            if (editingPartenaire) {
                const { error } = await supabase
                    .from('fd100_partenaires')
                    .update(dataToSave)
                    .eq('id', editingPartenaire.id);

                if (error) throw error;
                toast.success('Partenaire mis à jour');
            } else {
                const { error } = await supabase
                    .from('fd100_partenaires')
                    .insert(dataToSave);

                if (error) {
                    if (error.code === '23505') {
                        toast.error('Ce partenaire existe déjà');
                        return;
                    }
                    throw error;
                }
                toast.success('Partenaire créé');
            }

            setShowModal(false);
            setEditingPartenaire(null);
            await fetchData();
        } catch (error) {
            console.error('Error saving partenaire:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Helper: check if a partenaire is linked to at least one "espace" project
    const isLinkedToEspaceProject = useCallback((partenaire) => {
        const winningIds = getWinningProjectIds(partenaire.linked_project_ids);
        return winningIds.some(id => {
            const wp = winningProjects.find(w => w.projectId === id);
            return wp?.project?.type === 'espace';
        });
    }, [winningProjects]);

    // Toggle pieces flag for a partenaire
    const togglePieces = async (partenaire) => {
        try {
            const newValue = !partenaire.pieces;
            const { error } = await supabase
                .from('fd100_partenaires')
                .update({ pieces: newValue })
                .eq('id', partenaire.id);

            if (error) throw error;

            setPartenaires(prev => prev.map(p =>
                p.id === partenaire.id ? { ...p, pieces: newValue } : p
            ));
        } catch (error) {
            console.error('Error toggling pieces:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // Toggle no_email flag for a partenaire
    const toggleNoEmail = async (partenaire) => {
        try {
            const newValue = !partenaire.no_email;
            const { error } = await supabase
                .from('fd100_partenaires')
                .update({ no_email: newValue })
                .eq('id', partenaire.id);

            if (error) throw error;

            // Update local state
            setPartenaires(prev => prev.map(p =>
                p.id === partenaire.id ? { ...p, no_email: newValue } : p
            ));
        } catch (error) {
            console.error('Error toggling no_email:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // Delete partenaire
    const deletePartenaire = async (partenaire) => {
        if (!window.confirm(`Supprimer ${partenaire.name} ?`)) return;

        try {
            const { error } = await supabase
                .from('fd100_partenaires')
                .delete()
                .eq('id', partenaire.id);

            if (error) throw error;
            toast.success('Partenaire supprimé');
            await fetchData();
        } catch (error) {
            console.error('Error deleting partenaire:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    // Open edit modal
    const openEditModal = (partenaire) => {
        setEditingPartenaire(partenaire);
        // Convert legacy single values to arrays if needed
        const emails = partenaire.emails || (partenaire.email ? [partenaire.email] : ['']);
        const logoUrls = partenaire.logo_urls || (partenaire.logo_url ? [partenaire.logo_url] : ['']);
        setFormData({
            name: partenaire.name,
            type: partenaire.type,
            emails: emails.length > 0 ? emails : [''],
            logo_urls: logoUrls.length > 0 ? logoUrls : [''],
            linked_project_ids: partenaire.linked_project_ids || []
        });
        setShowModal(true);
    };

    // Open create modal
    const openCreateModal = () => {
        setEditingPartenaire(null);
        setFormData({
            name: '',
            type: activeTab,
            emails: [''],
            logo_urls: [''],
            linked_project_ids: []
        });
        setShowModal(true);
    };

    // Toggle project link
    const toggleProjectLink = (projectId) => {
        setFormData(prev => ({
            ...prev,
            linked_project_ids: prev.linked_project_ids.includes(projectId)
                ? prev.linked_project_ids.filter(id => id !== projectId)
                : [...prev.linked_project_ids, projectId]
        }));
    };

    // Helper: filter linked_project_ids to only include winning projects
    const getWinningProjectIds = (linkedIds) => {
        if (!linkedIds || !Array.isArray(linkedIds)) return [];
        return linkedIds.filter(id => WINNER_PROJECT_IDS.includes(id));
    };

    // Helper to get display project name (edited or original) - matches Excel export logic
    const getDisplayProjectName = (projectId) => {
        const projectData = winningProjects.find(wp => wp.projectId === projectId);
        if (!projectData) return 'Projet inconnu';
        const edits = editedDataMap[projectId];
        return edits?.projectName || projectData.projectName;
    };

    // Helper to get display creator name (edited or original) - matches Excel export logic
    const getDisplayCreatorName = (projectId) => {
        const projectData = winningProjects.find(wp => wp.projectId === projectId);
        if (!projectData) return '';
        const edits = editedDataMap[projectId];
        return edits?.creatorName || projectData.designerName;
    };

    // Filter partenaires - only show those with at least one winning project linked (except lieux/institutions/officiels which may not have projects)
    const filteredPartenaires = partenaires
        .filter(p => {
            // For the merged tab, include both distributeurs and editeurs
            if (activeTab === 'distributeur') {
                return p.type === 'distributeur' || p.type === 'editeur';
            }
            return p.type === activeTab;
        })
        .filter(p => {
            // Lieux, institutions and officiels don't need linked projects (they are external partners)
            if (p.type === 'lieu' || p.type === 'institution' || p.type === 'officiel') return true;
            // Only show partenaires that have at least one winning project
            const winningIds = getWinningProjectIds(p.linked_project_ids);
            return winningIds.length > 0;
        })
        .filter(p => {
            const search = searchTerm.toLowerCase();
            const contact = fabricantContactsMap[p.name.toLowerCase()] || '';
            return p.name.toLowerCase().includes(search) ||
                   (p.email && p.email.toLowerCase().includes(search)) ||
                   contact.toLowerCase().includes(search);
        })
        .sort((a, b) => a.name.localeCompare(b.name));

    // Stats - only count partenaires with winning projects (except lieux/institutions/officiels which don't need projects)
    const partenairesWithWinners = partenaires.filter(p =>
        p.type === 'lieu' || p.type === 'institution' || p.type === 'officiel' || getWinningProjectIds(p.linked_project_ids).length > 0
    );

    // Helper to check if partenaire has logo(s)
    const hasLogo = (p) => (p.logo_urls && p.logo_urls.length > 0) || p.logo_url;
    // Helper to check if partenaire has email(s)
    const hasEmail = (p) => (p.emails && p.emails.length > 0) || p.email;
    // Helper to check if partenaire uploaded their own logo
    const hasUploadedLogo = (p) => p.has_uploaded_logo === true;

    const fabricants = partenairesWithWinners.filter(p => p.type === 'fabricant');
    const ecoles = partenairesWithWinners.filter(p => p.type === 'ecole');
    const distributeursEditeurs = partenairesWithWinners.filter(p => p.type === 'distributeur' || p.type === 'editeur');
    const lieux = partenairesWithWinners.filter(p => p.type === 'lieu');
    const institutions = partenairesWithWinners.filter(p => p.type === 'institution');
    const officiels = partenairesWithWinners.filter(p => p.type === 'officiel');

    const stats = {
        totalFabricants: fabricants.length,
        totalEcoles: ecoles.length,
        totalDistributeursEditeurs: distributeursEditeurs.length,
        totalLieux: lieux.length,
        totalInstitutions: institutions.length,
        totalOfficiels: officiels.length,
        // Detailed stats per type
        fabricantsWithLogos: fabricants.filter(hasLogo).length,
        fabricantsWithEmails: fabricants.filter(hasEmail).length,
        fabricantsUploaded: fabricants.filter(hasUploadedLogo).length,
        ecolesWithLogos: ecoles.filter(hasLogo).length,
        ecolesWithEmails: ecoles.filter(hasEmail).length,
        ecolesUploaded: ecoles.filter(hasUploadedLogo).length,
        distEdWithLogos: distributeursEditeurs.filter(hasLogo).length,
        distEdWithEmails: distributeursEditeurs.filter(hasEmail).length,
        distEdUploaded: distributeursEditeurs.filter(hasUploadedLogo).length,
        lieuxWithLogos: lieux.filter(hasLogo).length,
        lieuxWithEmails: lieux.filter(hasEmail).length,
        lieuxUploaded: lieux.filter(hasUploadedLogo).length,
        institutionsWithLogos: institutions.filter(hasLogo).length,
        institutionsWithEmails: institutions.filter(hasEmail).length,
        institutionsUploaded: institutions.filter(hasUploadedLogo).length,
        officielsWithLogos: officiels.filter(hasLogo).length,
        officielsWithEmails: officiels.filter(hasEmail).length,
        officielsUploaded: officiels.filter(hasUploadedLogo).length,
        // Totals
        withLogos: partenairesWithWinners.filter(hasLogo).length,
        withEmails: partenairesWithWinners.filter(hasEmail).length,
        uploaded: partenairesWithWinners.filter(hasUploadedLogo).length
    };

    // Export partenaires to XLSX
    const exportToXLSX = () => {
        // Find max number of linked projects to determine column count
        const maxProjects = Math.max(...filteredPartenaires.map(p =>
            getWinningProjectIds(p.linked_project_ids).length
        ), 0);

        const dataToExport = filteredPartenaires.map(p => {
            const emails = p.emails || (p.email ? [p.email] : []);
            const winningIds = getWinningProjectIds(p.linked_project_ids);

            const row = {
                'Nom': p.name,
                'Contact': activeTab === 'fabricant' ? ([p.contact_prenom, p.contact_nom].filter(Boolean).join(' ') || fabricantContactsMap[p.name.toLowerCase()] || '') : '',
                'Tel. Contact': activeTab === 'fabricant' ? (p.contact_telephone || '') : '',
                'Email(s)': emails.join(', ') || ''
            };

            // Add each linked project with its designer name in separate columns
            for (let i = 0; i < maxProjects; i++) {
                const projectId = winningIds[i];
                if (projectId) {
                    row[`Designer ${i + 1}`] = getDisplayCreatorName(projectId);
                    row[`Projet associé ${i + 1}`] = getDisplayProjectName(projectId);
                } else {
                    row[`Designer ${i + 1}`] = '';
                    row[`Projet associé ${i + 1}`] = '';
                }
            }

            return row;
        });

        const tabName = activeTab === 'fabricant' ? 'Fabricants' :
                        activeTab === 'ecole' ? 'Écoles' :
                        activeTab === 'lieu' ? 'Lieux' :
                        activeTab === 'institution' ? 'Institutions' :
                        activeTab === 'officiel' ? 'Partenaires_Officiels' :
                        'Distributeurs_Éditeurs';

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, tabName);
        XLSX.writeFile(workbook, `FD100_${tabName}_${new Date().toISOString().split('T')[0]}.xlsx`);

        toast.success(`${filteredPartenaires.length} partenaires exportés`);
    };

    if (loading) return <Spinner />;

    return (
        <Container>
            <BackButton onClick={() => navigate('/admin/events')}>
                ← Retour aux événements
            </BackButton>

            <Header>
                <h1>Gestion des Partenaires FD100 2025</h1>
                <div className="subtitle">Fabricants et écoles liés aux projets lauréats</div>
            </Header>

            <StatsSection>
                <StatCard color="#e3f2fd" textColor="#1976d2">
                    <div className="value">{stats.totalFabricants}</div>
                    <div className="label">Fabricants</div>
                    <div className="detail">
                        <span className="detail-item logo">{stats.fabricantsWithLogos} logos ({stats.fabricantsUploaded} uploadés)</span>
                        <span className="detail-item email">{stats.fabricantsWithEmails} emails</span>
                    </div>
                </StatCard>
                <StatCard color="#f3e5f5" textColor="#7b1fa2">
                    <div className="value">{stats.totalEcoles}</div>
                    <div className="label">Écoles</div>
                    <div className="detail">
                        <span className="detail-item logo">{stats.ecolesWithLogos} logos ({stats.ecolesUploaded} uploadés)</span>
                        <span className="detail-item email">{stats.ecolesWithEmails} emails</span>
                    </div>
                </StatCard>
                <StatCard color="#e0f2f1" textColor="#00796b">
                    <div className="value">{stats.totalDistributeursEditeurs}</div>
                    <div className="label">Galeries & Éditeurs</div>
                    <div className="detail">
                        <span className="detail-item logo">{stats.distEdWithLogos} logos ({stats.distEdUploaded} uploadés)</span>
                        <span className="detail-item email">{stats.distEdWithEmails} emails</span>
                    </div>
                </StatCard>
                <StatCard color="#fff8e1" textColor="#f9a825">
                    <div className="value">{stats.totalLieux}</div>
                    <div className="label">Lieux</div>
                    <div className="detail">
                        <span className="detail-item logo">{stats.lieuxWithLogos} logos ({stats.lieuxUploaded} uploadés)</span>
                        <span className="detail-item email">{stats.lieuxWithEmails} emails</span>
                    </div>
                </StatCard>
                <StatCard color="#fff3e0" textColor="#e65100">
                    <div className="value">{stats.totalInstitutions}</div>
                    <div className="label">Partenaires Jurys</div>
                    <div className="detail">
                        <span className="detail-item logo">{stats.institutionsWithLogos} logos ({stats.institutionsUploaded} uploadés)</span>
                        <span className="detail-item email">{stats.institutionsWithEmails} emails</span>
                    </div>
                </StatCard>
                <StatCard color="#e8eaf6" textColor="#3f51b5">
                    <div className="value">{stats.totalOfficiels}</div>
                    <div className="label">Partenaires Officiels</div>
                    <div className="detail">
                        <span className="detail-item logo">{stats.officielsWithLogos} logos ({stats.officielsUploaded} uploadés)</span>
                        <span className="detail-item email">{stats.officielsWithEmails} emails</span>
                    </div>
                </StatCard>
                <StatCard color="#f5f5f5" textColor="#333">
                    <div className="value">{stats.withLogos}<span className="total-suffix">/{partenairesWithWinners.length}</span></div>
                    <div className="label">Total avec logos</div>
                    <div className="detail">
                        <span className="detail-item">{Math.round(stats.withLogos / partenairesWithWinners.length * 100) || 0}% complet</span>
                        <span className="detail-item" style={{ background: '#d4edda', color: '#155724' }}>✓ {stats.uploaded} uploadés</span>
                    </div>
                </StatCard>
                <StatCard color="#f5f5f5" textColor="#333">
                    <div className="value">{stats.withEmails}<span className="total-suffix">/{partenairesWithWinners.length}</span></div>
                    <div className="label">Total avec emails</div>
                    <div className="detail">
                        <span className="detail-item">{Math.round(stats.withEmails / partenairesWithWinners.length * 100) || 0}% complet</span>
                    </div>
                </StatCard>
            </StatsSection>

            <TabsContainer>
                <Tab active={activeTab === 'fabricant'} onClick={() => setActiveTab('fabricant')}>
                    Fabricants ({stats.totalFabricants})
                </Tab>
                <Tab active={activeTab === 'ecole'} onClick={() => setActiveTab('ecole')}>
                    Écoles ({stats.totalEcoles})
                </Tab>
                <Tab active={activeTab === 'distributeur'} onClick={() => setActiveTab('distributeur')}>
                    Galeries & Éditeurs ({stats.totalDistributeursEditeurs})
                </Tab>
                <Tab active={activeTab === 'lieu'} onClick={() => setActiveTab('lieu')}>
                    Lieux ({stats.totalLieux})
                </Tab>
                <Tab active={activeTab === 'institution'} onClick={() => setActiveTab('institution')}>
                    Partenaires Jurys ({stats.totalInstitutions})
                </Tab>
                <Tab active={activeTab === 'officiel'} onClick={() => setActiveTab('officiel')}>
                    Partenaires Officiels ({stats.totalOfficiels})
                </Tab>
            </TabsContainer>

            <TableSection>
                <TableHeader>
                    <h2>{activeTab === 'fabricant' ? 'Fabricants' : activeTab === 'ecole' ? 'Écoles' : activeTab === 'lieu' ? 'Lieux' : activeTab === 'institution' ? 'Partenaires Jurys' : activeTab === 'officiel' ? 'Partenaires Officiels' : 'Galeries & Éditeurs'} ({filteredPartenaires.length})</h2>
                    <HeaderActions>
                        <SearchInput
                            type="text"
                            placeholder="Rechercher par nom ou email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <ActionButton onClick={exportToXLSX} disabled={filteredPartenaires.length === 0}>
                            📥 Export XLSX
                        </ActionButton>
                        <ActionButton onClick={syncPartenaires} disabled={syncing}>
                            {syncing ? 'Synchronisation...' : '↻ Sync depuis projets'}
                        </ActionButton>
                        <ActionButton variant="primary" onClick={openCreateModal}>
                            + Ajouter
                        </ActionButton>
                    </HeaderActions>
                </TableHeader>

                {filteredPartenaires.length === 0 ? (
                    <EmptyState>
                        <div className="icon">{activeTab === 'fabricant' ? '🏭' : activeTab === 'ecole' ? '🎓' : activeTab === 'lieu' ? '📍' : activeTab === 'institution' ? '🏛️' : activeTab === 'officiel' ? '⭐' : '🏪'}</div>
                        <div className="message">Aucun {activeTab === 'fabricant' ? 'fabricant' : activeTab === 'ecole' ? 'école' : activeTab === 'lieu' ? 'lieu' : activeTab === 'institution' ? 'partenaire jury' : activeTab === 'officiel' ? 'partenaire officiel' : 'galerie ou éditeur'} trouvé</div>
                        <div className="hint">{activeTab === 'lieu' || activeTab === 'institution' || activeTab === 'officiel' ? 'Ajoutez des partenaires manuellement' : 'Utilisez "Sync depuis projets" pour importer automatiquement'}</div>
                    </EmptyState>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <Table>
                            <thead>
                                <tr>
                                    <th style={{ width: 80 }}>Logo</th>
                                    <th>Nom</th>
                                    {activeTab === 'fabricant' && (
                                        <th>Contact</th>
                                    )}
                                    <th>Email</th>
                                    <th style={{ width: 100, textAlign: 'center' }}>Pas d'email</th>
                                    {(activeTab === 'fabricant' || activeTab === 'distributeur') && (
                                        <th style={{ width: 80, textAlign: 'center' }}>Pièces</th>
                                    )}
                                    {activeTab === 'fabricant' && (
                                        <th style={{ width: 100, textAlign: 'center' }}>Visité</th>
                                    )}
                                    <th style={{ minWidth: 280 }}>Projets liés</th>
                                    <th style={{ width: 120 }}>Lien espace</th>
                                    <th style={{ width: 180 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPartenaires.map((partenaire) => (
                                    <tr key={partenaire.id}>
                                        <td>
                                            <LogoCell style={{ border: partenaire.has_uploaded_logo ? '2px solid #28a745' : undefined }}>
                                                {(() => {
                                                    // Get logos from logo_urls or fallback to legacy logo_url
                                                    const logoUrls = partenaire.logo_urls || (partenaire.logo_url ? [partenaire.logo_url] : []);
                                                    const firstLogo = logoUrls[0];
                                                    const extraLogos = logoUrls.length > 1 ? logoUrls.length - 1 : 0;
                                                    return firstLogo ? (
                                                        <div style={{ position: 'relative' }}>
                                                            <img src={firstLogo} alt={partenaire.name} />
                                                            {partenaire.has_uploaded_logo && (
                                                                <span style={{
                                                                    position: 'absolute',
                                                                    top: '-6px',
                                                                    left: '-6px',
                                                                    background: '#28a745',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    fontWeight: 'bold',
                                                                    padding: '2px 5px',
                                                                    borderRadius: '10px',
                                                                    whiteSpace: 'nowrap'
                                                                }} title={partenaire.logo_uploaded_at ? `Uploadé le ${new Date(partenaire.logo_uploaded_at).toLocaleDateString('fr-FR')}` : 'Uploadé par le partenaire'}>✓</span>
                                                            )}
                                                            {extraLogos > 0 && (
                                                                <span style={{
                                                                    position: 'absolute',
                                                                    bottom: '-4px',
                                                                    right: '-4px',
                                                                    background: '#007bff',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    fontWeight: 'bold',
                                                                    padding: '2px 5px',
                                                                    borderRadius: '10px',
                                                                    minWidth: '18px',
                                                                    textAlign: 'center'
                                                                }}>+{extraLogos}</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="placeholder">
                                                            {partenaire.type === 'fabricant' ? '🏭' : partenaire.type === 'ecole' ? '🎓' : partenaire.type === 'lieu' ? '📍' : partenaire.type === 'institution' ? '🏛️' : partenaire.type === 'officiel' ? '⭐' : '🏪'}
                                                        </span>
                                                    );
                                                })()}
                                            </LogoCell>
                                        </td>
                                        <td>
                                            <strong>{partenaire.name}</strong>
                                        </td>
                                        {activeTab === 'fabricant' && (
                                            <td style={{ fontSize: 13 }}>
                                                {(() => {
                                                    // Prefer Supabase contact data, fallback to project-extracted contact
                                                    const supabaseContact = [partenaire.contact_prenom, partenaire.contact_nom].filter(Boolean).join(' ');
                                                    const projectContact = fabricantContactsMap[partenaire.name.toLowerCase()];
                                                    const displayContact = supabaseContact || projectContact;
                                                    if (!displayContact) return <span style={{ color: '#ccc' }}>—</span>;
                                                    return (
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>{displayContact}</div>
                                                            {partenaire.contact_email && (
                                                                <div style={{ fontSize: 11, color: '#666' }}>{partenaire.contact_email}</div>
                                                            )}
                                                            {partenaire.contact_telephone && (
                                                                <div style={{ fontSize: 11, color: '#888' }}>{partenaire.contact_telephone}</div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        )}
                                        <td style={{ color: (partenaire.emails?.length > 0 || partenaire.email) ? '#333' : '#ccc' }}>
                                            {(() => {
                                                // Get emails from emails array or fallback to legacy email
                                                const emails = partenaire.emails || (partenaire.email ? [partenaire.email] : []);
                                                return emails.length > 0 ? emails.join(', ') : '—';
                                            })()}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={partenaire.no_email || false}
                                                onChange={() => toggleNoEmail(partenaire)}
                                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                title={partenaire.no_email ? 'Cliquer pour autoriser les emails' : 'Cliquer pour bloquer les emails'}
                                            />
                                        </td>
                                        {(activeTab === 'fabricant' || activeTab === 'distributeur') && (
                                            <td style={{ textAlign: 'center' }}>
                                                {isLinkedToEspaceProject(partenaire) ? (
                                                    <input
                                                        type="checkbox"
                                                        checked={partenaire.pieces || false}
                                                        onChange={() => togglePieces(partenaire)}
                                                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                        title={partenaire.pieces ? 'Pièces: oui' : 'Pièces: non'}
                                                    />
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>—</span>
                                                )}
                                            </td>
                                        )}
                                        {activeTab === 'fabricant' && (
                                            <td style={{ textAlign: 'center', fontSize: 12 }}>
                                                {partenaire.last_visited_at ? (
                                                    <span style={{ color: '#27ae60' }} title={new Date(partenaire.last_visited_at).toLocaleString('fr-FR')}>
                                                        {new Date(partenaire.last_visited_at).toLocaleDateString('fr-FR')}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>—</span>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            {(() => {
                                                // Only show winning project IDs
                                                const winningIds = getWinningProjectIds(partenaire.linked_project_ids);
                                                return (
                                                    <ProjectsContainer>
                                                        <ProjectCount count={winningIds.length}>
                                                            {winningIds.length}
                                                        </ProjectCount>
                                                        <ProjectThumbnails>
                                                            {winningIds.slice(0, 6).map(projectId => {
                                                                const projectData = winningProjects.find(wp => wp.projectId === projectId);
                                                                // Privacy option only applies to fabricants, not écoles
                                                                const isPrivate = activeTab === 'fabricant' && projectData?.communiquerFabricants?.toLowerCase() === 'non';
                                                                return (
                                                                    <ProjectThumbnailWrapper key={projectId}>
                                                                        <ProjectThumbnail isPrivate={isPrivate}>
                                                                            {projectData?.imageUrl ? (
                                                                                <img src={projectData.imageUrl} alt={projectData.projectName} />
                                                                            ) : (
                                                                                <div className="placeholder">?</div>
                                                                            )}
                                                                            {isPrivate && <div className="privacy-icon">🔒</div>}
                                                                        </ProjectThumbnail>
                                                                        <Tooltip>
                                                                            <div>{getDisplayProjectName(projectId)}</div>
                                                                            <div style={{ opacity: 0.7 }}>{getDisplayCreatorName(projectId)}</div>
                                                                            {isPrivate && <div className="privacy-note">Ne souhaite pas communiquer</div>}
                                                                        </Tooltip>
                                                                    </ProjectThumbnailWrapper>
                                                                );
                                                            })}
                                                            {winningIds.length > 6 && (
                                                                <ProjectThumbnailWrapper>
                                                                    <ProjectThumbnail as="div" style={{ background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: '#666', border: '2px solid #ddd' }}>
                                                                        +{winningIds.length - 6}
                                                                    </ProjectThumbnail>
                                                                </ProjectThumbnailWrapper>
                                                            )}
                                                        </ProjectThumbnails>
                                                    </ProjectsContainer>
                                                );
                                            })()}
                                        </td>
                                        <td>
                                            <LinkButton
                                                href={`/partenaires?id=${partenaire.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Voir espace →
                                            </LinkButton>
                                        </td>
                                        <td>
                                            <ActionButtonSmall onClick={() => openEditModal(partenaire)}>
                                                ✎ Modifier
                                            </ActionButtonSmall>
                                            <ActionButtonSmall
                                                variant="danger"
                                                onClick={() => deletePartenaire(partenaire)}
                                            >
                                                ✕ Suppr.
                                            </ActionButtonSmall>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                <LastUpdate>
                    Dernière actualisation : {lastUpdate?.toLocaleString('fr-FR')}
                </LastUpdate>
            </TableSection>

            {/* Create/Edit Modal */}
            {showModal && (
                <ModalOverlay onClick={() => setShowModal(false)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>{editingPartenaire ? 'Modifier le partenaire' : 'Ajouter un partenaire'}</h3>
                            <CloseButton onClick={() => setShowModal(false)}>&times;</CloseButton>
                        </ModalHeader>

                        <FormGroup>
                            <label>Nom *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Nom du partenaire"
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Type</label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                            >
                                <option value="fabricant">Fabricant</option>
                                <option value="ecole">École</option>
                                <option value="distributeur">Distributeur</option>
                                <option value="editeur">Galerie / Éditeur</option>
                                <option value="lieu">Lieu</option>
                                <option value="institution">Institution</option>
                                <option value="officiel">Partenaire Officiel</option>
                            </select>
                        </FormGroup>

                        <FormGroup>
                            <label>Emails ({formData.emails.filter(e => e.trim()).length})</label>
                            {formData.emails.map((email, index) => (
                                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => updateEmail(index, e.target.value)}
                                        placeholder="email@exemple.com"
                                        style={{ flex: 1 }}
                                    />
                                    {formData.emails.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeEmailField(index)}
                                            style={{
                                                background: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '0 12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addEmailField}
                                style={{
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                + Ajouter un email
                            </button>
                        </FormGroup>

                        <FormGroup>
                            <label>Logos ({formData.logo_urls.filter(url => url).length}) - Le premier est affiché</label>
                            {formData.logo_urls.map((logoUrl, index) => (
                                <div key={index} style={{ marginBottom: '12px' }}>
                                    <input
                                        type="file"
                                        id={`logo-upload-${index}`}
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleLogoUpload(e, index)}
                                    />
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <LogoUploadArea
                                            className={logoUrl ? 'has-logo' : ''}
                                            onClick={() => document.getElementById(`logo-upload-${index}`).click()}
                                            style={{ flex: 1 }}
                                        >
                                            {uploadingLogo === index ? (
                                                <Spinner />
                                            ) : logoUrl ? (
                                                <>
                                                    <img src={logoUrl} alt={`Logo ${index + 1}`} />
                                                    <div className="change-text">
                                                        {index === 0 ? 'Logo principal - Cliquer pour changer' : 'Cliquer pour changer'}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="upload-text">
                                                    {index === 0 ? '📤 Logo principal (affiché)' : `📤 Logo ${index + 1} (backup)`}
                                                </div>
                                            )}
                                        </LogoUploadArea>
                                        {formData.logo_urls.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeLogoField(index)}
                                                style={{
                                                    background: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '8px 12px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addLogoField}
                                style={{
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                + Ajouter un logo
                            </button>
                        </FormGroup>

                        <FormGroup>
                            <label>Projets liés ({formData.linked_project_ids.length})</label>
                            <ProjectsList>
                                {/* Sort projects: linked projects first, then alphabetically */}
                                {[...winningProjects]
                                    .sort((a, b) => {
                                        const aLinked = formData.linked_project_ids.includes(a.projectId);
                                        const bLinked = formData.linked_project_ids.includes(b.projectId);
                                        if (aLinked && !bLinked) return -1;
                                        if (!aLinked && bLinked) return 1;
                                        return (a.projectName || '').localeCompare(b.projectName || '');
                                    })
                                    .map(({ projectId, projectName, designerName }) => (
                                    <ProjectItem key={projectId} isLinked={formData.linked_project_ids.includes(projectId)}>
                                        <input
                                            type="checkbox"
                                            checked={formData.linked_project_ids.includes(projectId)}
                                            onChange={() => toggleProjectLink(projectId)}
                                        />
                                        <div className="project-info">
                                            <div className="name">{projectName}</div>
                                            <div className="designer">{designerName}</div>
                                        </div>
                                    </ProjectItem>
                                ))}
                            </ProjectsList>
                        </FormGroup>

                        <ModalFooter>
                            <ActionButton onClick={() => setShowModal(false)}>
                                Annuler
                            </ActionButton>
                            <ActionButton
                                variant="primary"
                                onClick={savePartenaire}
                                disabled={saving}
                            >
                                {saving ? 'Enregistrement...' : editingPartenaire ? 'Mettre à jour' : 'Créer'}
                            </ActionButton>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
};

export default FD100Partenaires;
